import React from 'react';

import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { ModalHeader } from '@lightspeed/flame/Modal';
import { Text } from '@lightspeed/flame/Text';
import { useSelector } from 'react-redux';

import { HubLabelPrintError } from 'label/HubPrint';
import { PRINTER_STATUSES } from 'pages/views/devices/socketClient/types';
import { PRINTER_KINDS } from 'root/apps/pages/views/devices/types';
import { receiptPreviewFrame, goodPrinterStatusArray } from 'root/merchantos/print';

import { setHubPrintLoading } from '..';

import { StyledModal, MaxSizedImage } from './Print.style';

export const DEFAULT_ERROR_MESSAGE =
  "An unexpected error occurred and the print request couldn't be completed";

interface PrintErrorProps {
  error?: string;
  kind: PRINTER_KINDS;
}

interface HubPrintIframe extends HTMLIFrameElement {
  contentWindow: (Window & { originalPrint?: () => void }) | null;
}

const PrintError: React.FC<PrintErrorProps> = ({ error, kind }) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(true);
  let iframe: HubPrintIframe | null = null;
  let receiptPrintUrl: string | null = null;

  const modal = {
    HEADER: `Unable to print ${kind ? kind.toLowerCase() : ''}`,
    BODY: 'Make sure Lightspeed Hub is connected to Retail.',
    BUTTON: {
      TRY_AGAIN: 'Try again',
      BROWSER_PRINT: 'Print with browser',
      DISMISS: 'Dismiss',
    },
  };

  const printer = useSelector(state => state.hub.defaultPrinters[kind]);
  const status = printer?.status;

  const goodPrinterStatus = goodPrinterStatusArray.includes(status!);

  if (kind === PRINTER_KINDS.RECEIPT) {
    iframe = document.getElementById(receiptPreviewFrame) as HubPrintIframe;
    receiptPrintUrl = iframe.getAttribute('src');
  }

  return (
    <StyledModal
      isOpen={isOpen}
      onRequestClose={() => {
        setIsOpen(false);
      }}
    >
      <ModalHeader data-test="print-error-modal-header">{modal.HEADER}</ModalHeader>
      <Alert type="warning" noCloseBtn mt={3} mr={3} ml={3}>
        {error || (
          <>
            {status && !goodPrinterStatus && (
              <Text mb={1} fontWeight="bold">
                Printer {status.replace('_', ' ').toLowerCase()}
              </Text>
            )}
            {DEFAULT_ERROR_MESSAGE}
          </>
        )}
      </Alert>
      {kind !== PRINTER_KINDS.LABEL && (
        <Flex flexDirection="column" justifyContent="center" textAlign="center" mt={3}>
          <Box textAlign="center">
            <MaxSizedImage src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-9919532/images/devices/hub-connected-retail.png" alt={modal.BODY} />
          </Box>
          {!status ? <Box mt={6}>{modal.BODY}</Box> : null}
        </Flex>
      )}

      {kind === PRINTER_KINDS.LABEL && (
        <HubLabelPrintError
          labelPrinterStatus={status || PRINTER_STATUSES.ERROR}
          includeHeader={false}
        />
      )}

      <Flex justifyContent="center" flexDirection="column" p={3}>
        {kind === PRINTER_KINDS.RECEIPT && (
          <>
            <Button
              disabled={!goodPrinterStatus || !receiptPrintUrl}
              m={1}
              variant="secondary"
              fill
              onClick={() => {
                setHubPrintLoading({ el: iframe, url: receiptPrintUrl });
                window.merchantos.print.handlePrintReceipt({ url: receiptPrintUrl! });
              }}
            >
              {modal.BUTTON.TRY_AGAIN}
            </Button>
            <Button
              disabled={!iframe?.contentWindow?.originalPrint}
              m={1}
              onClick={() => {
                iframe?.contentWindow?.originalPrint?.();
                setIsOpen(false);
              }}
            >
              {modal.BUTTON.BROWSER_PRINT}
            </Button>
          </>
        )}
        <Button
          m={1}
          onClick={() => {
            setIsOpen(false);
          }}
        >
          {modal.BUTTON.DISMISS}
        </Button>
      </Flex>
    </StyledModal>
  );
};

export default PrintError;
